<template>
  <ModalLayout :title="isEdit ? 'Edit Geofence Type' : 'Add Geofence Type'">
    <div
      class="relative rounded-t-[10px] bg-white px-[24px] pt-[41px] sm:px-[40px]"
    >
      <button
        type="button"
        aria-label="Close"
        class="absolute right-[24px] top-[36px] flex size-[32px] cursor-pointer items-center justify-center rounded-full border border-gpx_gray-800 sm:right-[40px]"
        @click.prevent="emit('close')"
      >
        <XMarkIcon
          class="heroicon-stroke-w-1.4 size-[20px] text-gpx_gray-200"
        />
      </button>

      <div class="border-b border-b-gpx_gray-800 pb-[18px]">
        <div class="mb-[2px] text-gpx_sm font-medium text-gpx_gray-200">
          Geofence Types
        </div>
        <div class="text-gpx_2xl font-semibold text-gpx_black-500">
          {{ isEdit ? 'Edit this type' : 'Add a type' }}
        </div>
      </div>

      <form class="mt-[35px]" @submit.prevent="handleSave">
        <UIInput
          v-model="name"
          label="Name"
          placeholder="e.g. Pediatric Clinic"
        />
        <label
          for="place_type_parent"
          class="mb-[3px] mt-[20px] block text-gpx_xs font-medium text-gpx_gray-200"
        >
          Standard type
        </label>
        <Select
          v-model="parent"
          input-id="place_type_parent"
          :options="standardTypes"
          option-label="name"
          option-value="slug"
          placeholder="Select a standard type"
          fluid
        />
        <p class="mt-[4px] text-gpx_xs text-gpx_gray-100">
          Reports and filters on the standard type include geofences of this
          type.
        </p>
      </form>
    </div>

    <div class="rounded-b-[10px] bg-white px-[24px] py-[32px] sm:px-[40px]">
      <Button
        size="lg"
        loadingText="Saving"
        :loading="isSaving"
        :disabled="isSaving || !name.trim() || !parent"
        @click="handleSave"
      >
        {{ isEdit ? 'Save' : 'Add geofence type' }}
      </Button>
    </div>
  </ModalLayout>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import Select from 'primevue/select';
import { Button } from '@gpx-spa/ui';
import {
  createAccountPlaceType,
  updateAccountPlaceType,
} from '@gpx-spa/queries';
import ModalLayout from '@/layouts/ModalLayout.vue';
import UIInput from '@/components/ui/UIInput.vue';
import { usePlaceTypes } from '@/composables/usePlaceTypes';

const props = defineProps({
  params: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const toast = useToast();
const createMutation = createAccountPlaceType();
const updateMutation = updateAccountPlaceType();
const { placeTypes } = usePlaceTypes();

const accountId = computed(() => store.getters['auth/accountId']);
const isEdit = computed(() => !!props.params?.id);
const standardTypes = computed(() =>
  placeTypes.value
    .filter((type) => !type.is_custom)
    .sort((a, b) => a.name.localeCompare(b.name)),
);

const name = ref(props.params?.name ?? '');
const parent = ref(props.params?.parent ?? null);
const isSaving = ref(false);

const handleSave = async () => {
  const trimmed = name.value.trim();
  if (isSaving.value || !trimmed || !parent.value) return;

  isSaving.value = true;
  try {
    if (isEdit.value) {
      await updateMutation.mutateAsync({
        accountId: accountId.value,
        id: props.params.id,
        name: trimmed,
        parent: parent.value,
      });
      toast.success('Geofence type saved');
    } else {
      await createMutation.mutateAsync({
        accountId: accountId.value,
        name: trimmed,
        parent: parent.value,
      });
      toast.success('Geofence type added');
    }
    emit('close');
  } catch (e) {
    toast.error(
      e.response?.data?.message ||
        'Could not save the geofence type. Please try again later.',
    );
  } finally {
    isSaving.value = false;
  }
};
</script>
